import { get, pagedFetch } from '../httpHelpers/submitter';
import { get as getPrimer } from '../httpHelpers/primer';
import {
  Series,
  EntryComponent,
  Assessment,
  ComponentBusinessId,
  Component,
  SectionBusinessId,
} from '@cambridgeassessment/lib-ee-commons';
import {
  ProcessingError,
  ReportDetails,
  ReportDetailsQuery,
  ReportSummary,
  SearchQuery,
  SimilarityParams,
} from '../../types/Plagiarism';
import { forEach } from 'lodash';
import { PagedFetchResult } from '../httpHelpers/pagedFetch';
import qs from 'qs';
import { SortingParams } from '../../pages/Content/MalpracticeReports/components/ReportTable/ReportTable';

export const getSeriesAssessments = (businessUnitId: string, seriesId: Series['id']): Promise<Assessment[]> =>
  get(`v1/plagiarism/businessUnit/${businessUnitId}/${seriesId}`);

export const getAssessmentComponents = (
  businessUnitId: string,
  seriesId: Series['id'],
  assessmentCode: Assessment['code'],
): Promise<EntryComponent[]> => get(`v1/plagiarism/businessUnit/${businessUnitId}/${seriesId}/${assessmentCode}`);

export const getReportSummaryData = (
  businessUnitId: string,
  { seriesId, ...sq }: SearchQuery,
  nextUrl?: string,
  sortingParams?: SortingParams<ReportSummary>,
): Promise<PagedFetchResult<ReportSummary>> => {
  const params = new URLSearchParams();
  forEach({ ...sq, ...sortingParams }, (value, key) => {
    if (value) {
      params.append(key, value.toString());
    }
  });

  return pagedFetch(
    `v1/plagiarism/businessUnit/${businessUnitId}/${seriesId}/reportSummary?${params.toString()}`,
    nextUrl,
  );
};

export const getReportDetailsComponents = ({
  seriesId,
  assessmentCode,
  componentNumber,
}: Omit<ComponentBusinessId, 'centreId'>): Promise<Component[]> =>
  getPrimer(
    `v1/id/components?seriesId=${seriesId}&assessmentCode=${assessmentCode}&componentNumber=${componentNumber}`,
  );

export const getReportDetailsData = (
  { businessUnitId, seriesId, assessmentCode, componentNumber, sectionId }: ReportDetailsQuery,
  { learnerNumber, centreId, low, high }: SearchQuery & SimilarityParams,
  sortingParams?: SortingParams<ReportDetails>,
  nextUrl?: string,
): Promise<PagedFetchResult<ReportDetails>> => {
  const query = qs.stringify(
    {
      fields: [
        'centreId',
        'learner',
        'learnerNumber',
        'similarityPercentage',
        'wordCount',
        'submissionId',
        'createdTime',
      ],
      learnerNumber,
      centreId,
      low,
      high,
      ...sortingParams,
    },
    { arrayFormat: 'repeat', encode: false },
  );

  return pagedFetch(
    `v1/plagiarism/businessUnit/${businessUnitId}/${seriesId}/${assessmentCode}/${componentNumber}/${sectionId}/reportDetails/similarityRange?${query}`,
    nextUrl,
  );
};

export const viewTurnitinReport = (submissionId: string): Promise<{ viewerUrl: string }> => {
  return get(`v1/plagiarism/${submissionId}/viewerUrl`);
};

export const getFailedReportDetails = async (
  {
    businessUnitId,
    seriesId,
    assessmentCode,
    componentNumber,
    sectionId,
  }: Omit<SectionBusinessId, 'learnerNumber' | 'centreId' | 'learnerUniqueId'>,
  queryParams: Partial<Pick<SectionBusinessId, 'learnerNumber' | 'centreId'>>,
  sortingParams?: SortingParams<ProcessingError>,
  nextUrl?: string,
): Promise<PagedFetchResult<ProcessingError>> => {
  const queryString = qs.stringify(
    {
      fields: ['centreId', 'learner', 'learnerNumber', 'error', 'createdTime', 'plagiarismReportState'],
      learnerNumber: queryParams.learnerNumber,
      centreId: queryParams.centreId,
      ...sortingParams,
    },
    { encode: false, arrayFormat: 'repeat', skipNulls: true },
  );

  return pagedFetch<ProcessingError>(
    `v1/plagiarism/businessUnit/${businessUnitId}/${seriesId}/${assessmentCode}/${componentNumber}/${sectionId}/reportDetails/error?${queryString}`,
    nextUrl,
  );
};
